import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";

import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { RepositoryInput } from "@/components/RepositoryInput";
import { RepositorySelectorPopover } from "@/components/RepositorySelectorPopover";
import { useSearchRepositoriesPaginated } from "@/queries/runner-queries";
import { SearchMode } from "gitpod-next-api/gitpod/v1/runner_pb";

type RepositorySelectorProps = {
    /** Runner ID for repository search */
    runnerId?: string;
    /** SCM host (e.g., "github.com") */
    scmHost: string;
    /** Current repository URL value */
    value?: string;
    /** Callback when repository URL changes */
    onChange: (url: string) => void;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Optional className for the container */
    className?: string;
    /** Label text */
    label?: string;
    /** Tracking location for analytics */
    trackingLocation?: string;
    /** Auto-switch to manual mode if no runner available */
    autoSwitchToManual?: boolean;
};

type SelectedRepo = {
    url: string;
    name: string;
};

export const RepositorySelector: FC<RepositorySelectorProps> = ({
    runnerId,
    scmHost,
    value,
    onChange,
    disabled = false,
    className,
    label: _label = "Choose a repository",
    trackingLocation = "repository-selector",
    autoSwitchToManual = false,
}) => {
    const [mode, setMode] = useState<"browse" | "manual">("browse");
    const [selectedRepo, setSelectedRepo] = useState<SelectedRepo | null>(null);
    const [currentValue, setCurrentValue] = useState(value || "");
    const currentValueRef = useRef(currentValue);
    const inputRef = useRef<HTMLInputElement>(null);

    // Query to check if user has any repositories available
    const { data: reposData, status: reposStatus } = useSearchRepositoriesPaginated({
        scmHost,
        runnerId: runnerId ?? "",
        query: "",
        searchMode: SearchMode.KEYWORD,
    });

    // Check if user has repos available
    // - undefined = still loading or error (let popover handle errors to show auth CTA)
    // - true = has repos
    // - false = no repos (empty list only)
    const hasRepos = useMemo(() => {
        if (reposStatus === "pending") return undefined; // Still loading
        if (reposStatus === "error") return undefined; // Let popover handle errors (shows auth CTA)
        const repos = reposData?.pages.flatMap((page) => page.repositories) ?? [];
        return repos.length > 0;
    }, [reposData, reposStatus]);

    // Keep ref in sync with state
    useEffect(() => {
        currentValueRef.current = currentValue;
    }, [currentValue]);

    // Focus input when in manual mode or when we know there are no repos
    useEffect(() => {
        if (mode === "manual" || hasRepos === false) {
            inputRef.current?.focus();
        }
    }, [mode, hasRepos]);

    // Auto-switch to manual mode when no runner is available or no repos
    useEffect(() => {
        if (autoSwitchToManual && mode === "browse") {
            setMode("manual");
        }
    }, [autoSwitchToManual, mode]);

    // Handle manual input change - update local state AND ref
    const handleManualInputChange = useCallback((newValue: string) => {
        setCurrentValue(newValue);
        // Update ref synchronously so it's available to handleValidationChange
        // which may be called before the useEffect runs
        currentValueRef.current = newValue;
    }, []);

    // Handle validation change - only propagate valid URLs to parent
    const handleValidationChange = useCallback(
        (valid: boolean) => {
            // Only propagate to parent when URL is syntactically valid
            // This prevents invalid URLs from triggering backend ParseContextURL calls
            // Use ref to get the latest value to avoid stale closure
            if (valid && currentValueRef.current) {
                onChange(currentValueRef.current);
            }
        },
        [onChange],
    );

    // Handle repository selection from popover
    const handleRepoSelect = useCallback(
        (repoUrl: string, repoName: string) => {
            setSelectedRepo({ url: repoUrl, name: repoName });
            setCurrentValue(repoUrl); // Sync local state when repo is selected
            setMode("browse");
            onChange(repoUrl);
        },
        [onChange],
    );

    // Handle manual entry mode activation
    const handleEnterManualMode = useCallback(() => {
        setMode("manual");
    }, []);

    // Handle back to browse mode
    const handleBackToBrowse = useCallback(() => {
        // Restore currentValue to parent's value when going back to browse
        setCurrentValue(value || "");
        setMode("browse");
    }, [value]);

    // Determine primary button label (only used in browse mode)
    const buttonLabel = useMemo(() => {
        if (selectedRepo) {
            return selectedRepo.name;
        }
        return "Select repository";
    }, [selectedRepo]);

    // If no repos available, show input directly (no dropdown)
    const showInputOnly = hasRepos === false;

    return (
        <div className={cn("flex flex-col gap-2", className)}>
            {/* <Label className="font-medium">{label}</Label> */}
            <div className={cn("flex items-start gap-3", disabled && "opacity-50")}>
                {showInputOnly ? (
                    // No repos: show text input directly
                    <RepositoryInput
                        ref={inputRef}
                        label=""
                        value={currentValue}
                        onChange={handleManualInputChange}
                        onValidationChange={handleValidationChange}
                        runnerId={runnerId}
                        disabled={disabled}
                        reserveErrorSpace={false}
                        className="flex-1 gap-0"
                    />
                ) : mode === "browse" ? (
                    <>
                        {/* Browse mode: Repository Selector dropdown */}
                        <RepositorySelectorPopover
                            runnerId={runnerId ?? ""}
                            scmHost={scmHost}
                            value={value}
                            onSelect={handleRepoSelect}
                            disabled={disabled}
                            buttonLabel={buttonLabel}
                            trackingLocation={trackingLocation}
                            triggerClassName="min-w-0 flex-1"
                        />
                        {/* "Or enter manually" link */}
                        <div className="flex h-9 shrink-0 items-center">
                            <button
                                type="button"
                                onClick={handleEnterManualMode}
                                disabled={disabled}
                                className="whitespace-nowrap text-sm text-content-secondary hover:text-content-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                data-tracking-id={`${trackingLocation}-enter-manual-mode`}
                            >
                                or enter manually
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Manual mode: Back button to return to browse */}
                        <Button
                            variant="secondary"
                            onClick={handleBackToBrowse}
                            disabled={disabled}
                            className="h-9 shrink-0 px-3"
                            data-tracking-id={`${trackingLocation}-back-to-browse`}
                        >
                            <ArrowLeft className="size-4" />
                        </Button>
                        {/* Manual input */}
                        <RepositoryInput
                            ref={inputRef}
                            label=""
                            value={currentValue}
                            onChange={handleManualInputChange}
                            onValidationChange={handleValidationChange}
                            runnerId={runnerId}
                            disabled={disabled}
                            reserveErrorSpace={false}
                            className="flex-1 gap-0"
                        />
                    </>
                )}
            </div>
        </div>
    );
};
